/**
 * @file Filter passed members
 */
import { User } from '../../generated/graphql';

import filter, { Options } from '../filter';

type Data = Pick<User, 'id' | 'firstName' | 'lastName' | 'status'>;

/**
 * Get full name key for match sorter options
 *
 * @param item Item to get the key for
 * @returns    Key as a full name string
 */
const getFullNameKey = (item: Data) => {
  const { firstName, lastName } = item;

  return `${firstName} ${lastName}`;
};

const options: Options<Data> = {
  keys: [getFullNameKey],
};

/**
 * Filter passed members
 *
 * First return all selected ones
 * (these are visible no matter the search)
 *
 * And then return the rest, filtered by what's typed into search
 *
 * @param search     What the user typed in search box
 * @param membersAll All the members available
 * @returns          Filtered array of members
 */
export const searchMembers = (search: string, membersAll: Data[]): Data[] => {
  // If nothing is searched, then just return everything
  if (search.trim().length === 0) {
    return membersAll;
  }
  const membersSearched = filter(membersAll, search, options);

  return membersSearched;
};

/**
 * Filter passed members
 * Only show assigned ones
 *
 * @param search     What the user typed in search box
 * @param membersAll All the members available
 * @param userIds    IDs of selected members
 * @returns          Filtered array of members
 */
export const searchGroupMembers = (
  search: string,
  membersAll: Data[],
  userIds: Set<User['id']>,
): Data[] => {
  const membersAssigned = membersAll.filter(user => userIds.has(user.id));
  // If nothing is searched, then just return everything
  if (search.trim().length === 0) {
    return membersAssigned;
  }

  return filter(membersAssigned, search, options);
};
